import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SpriteIcon } from '@/components/SpriteIcon';

/**
 * A labelled input for the auth forms, shaped to take `{...register(name)}` from
 * React Hook Form directly.
 *
 * The Razor pages used two variants of the same field: a plain
 * `form-control-lg`, and — on the change-password screen only — a `form-control`
 * inside an input group with a show/hide button. Both are here rather than in
 * two components, because everything else about them is identical.
 */
interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Validation message; renders in the same `text-danger small` span the Razor pages used. */
  error?: string;
  /** Right-aligned content on the label row — the login form puts "Forgot password?" there. */
  labelAside?: ReactNode;
  /** Small print between the input and the error. */
  hint?: ReactNode;
  /** Adds the show/hide password button. */
  toggle?: boolean;
  /** `form-control-lg`. Every screen except change-password used it. */
  large?: boolean;
  /** Live feedback while typing, as the confirm-password field had. */
  state?: 'valid' | 'invalid';
}

export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(function AuthField(
  { label, error, labelAside, hint, toggle = false, large = false, state, className, type, id, ...input },
  ref,
) {
  const { t } = useTranslation();
  const generatedId = useId();
  const [revealed, setRevealed] = useState(false);

  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const classes = ['form-control'];
  if (large) classes.push('form-control-lg');
  if (state === 'valid') classes.push('border-success', 'is-valid');
  if (state === 'invalid') classes.push('border-danger', 'is-invalid');
  if (className) classes.push(className);

  const control = (
    <input
      {...input}
      ref={ref}
      id={inputId}
      type={toggle && revealed ? 'text' : type}
      className={classes.join(' ')}
      aria-invalid={error ? true : undefined}
      aria-describedby={[hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined}
    />
  );

  return (
    <div className="mb-3">
      {labelAside ? (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label htmlFor={inputId} className="form-label mb-0">
            {label}
          </label>
          {labelAside}
        </div>
      ) : (
        <label htmlFor={inputId} className="form-label">
          {label}
        </label>
      )}

      {toggle ? (
        <div className="input-group">
          {control}
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => setRevealed((current) => !current)}
            aria-controls={inputId}
            aria-pressed={revealed}
            aria-label={
              revealed ? t('auth:Auth.HidePassword', 'Hide password') : t('auth:Auth.ShowPassword', 'Show password')
            }
          >
            <SpriteIcon name={revealed ? 'eye-off' : 'eye'} />
          </button>
        </div>
      ) : (
        control
      )}

      {hint && (
        <div id={hintId} className="form-text small">
          {hint}
        </div>
      )}

      {error && (
        <span id={errorId} className="text-danger small">
          {error}
        </span>
      )}
    </div>
  );
});
